import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { NewMorphemeLessonLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._context";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useFreeTierGate } from "@-/dev.improve.haniverse.components.superwall";
import { useFetchIsPinyinLearned } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.audio";

/**
 * Reveal a lesson card: flip to its mnemonics.
 *
 * Revealing is a VIEW change, not a study decision — it enrolls nothing. Enrollment happens when
 * the learner accepts the card with "Next Card" (the deck's `accept-lesson` action), so a
 * character they read and then skip until tomorrow was never added to study. The free-tier gate
 * is still consulted here so a learner is not shown a lesson for a character they cannot study
 * and paywalled only after reading it.
 */
export function useNewMorphemeLessonRevealPressMutation() {
  const [grapheme, introducedPinyinList, lessonPart, playSequence] = useContextSelectors(
    NewMorphemeLessonLayoutContext,
    (c) => c.grapheme,
    (c) => c.introducedPinyinList,
    (c) => c.lessonPart,
    (c) => c.playSequence,
  );
  const [flip] = useContextSelectors(LearnDeckContext, (c) => c.flip);
  const { canAddToStudy, openUpgradeToPremium } = useFreeTierGate("lesson_reveal");
  const fetchIsPinyinLearned = useFetchIsPinyinLearned();

  return useMutation({
    mutationFn: async () => {
      if (!canAddToStudy(grapheme)) {
        openUpgradeToPremium();
        return;
      }
      // The reveal effect runs once, here — never because the back face mounted. A meaning-only
      // lesson stays silent: its readings are introduced later, by the pronunciation level. Any
      // other lesson speaks the readings the learner can read: the ones it introduces, which teach
      // their sounds, and any other whose initial, final and tone the learner has already learned.
      if (lessonPart !== "meaning" && lessonPart !== "primitive") {
        void fetchIsPinyinLearned(introducedPinyinList).then((isPinyinLearned) => {
          playSequence(isPinyinLearned);
        });
      }
      flip("back");
    },
    mutationKey: ["newMorphemeLessonRevealPress"],
  });
}
